import moment from "moment";

/** PGI-S: speech severity (lower score = better) */
export const PGI_S_LABELS = {
  0: "No speech difficulty",
  1: "Mild",
  2: "Moderate",
  3: "Severe",
  4: "Very severe",
};

/** PGI-C: speech change since last session (lower = improved) */
export const PGI_C_LABELS = {
  1: "Much better",
  2: "A little better",
  3: "No change",
  4: "A little worse",
  5: "Much worse",
};

const buildPgiScaleText = (labelMap) =>
  Object.keys(labelMap)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => `${key} = ${labelMap[key]}`)
    .join(", ");

/** Matches React Native COLORS.BLUE_2 / COLORS.GREEN */
export const PGI_CHART_COLORS = {
  severity: "#0057F0",
  change: "#2E9E5B",
};

export const PGI_CHART_SCALES = {
  severity: {
    title: "How to read this scale for Speech Severity:",
    text: buildPgiScaleText(PGI_S_LABELS),
  },
  change: {
    title: "How to read this scale for Speech Change:",
    text: buildPgiScaleText(PGI_C_LABELS),
  },
};

const formatDate = (createdAt) => moment(createdAt).format("MM-DD-YYYY");

/**
 * Build one PGI series the same way as React Native SpeechRatingScreenLE:
 * chronological points with date string as x so same-day scores align vertically on that date.
 */
const buildSeriesData = (sortedGraphData, field) => {
  const points = sortedGraphData
    .filter(
      (row) => row[field] !== null && row[field] !== undefined && row[field] !== ""
    )
    .map((row) => ({
      x: formatDate(row.created_at),
      y: parseInt(row[field], 10),
    }));

  if (points.length === 0) {
    return { labels: [], data: [] };
  }

  const dateOrder = [];
  points.forEach((point) => {
    if (!dateOrder.includes(point.x)) {
      dateOrder.push(point.x);
    }
  });

  if (points.length === 1) {
    const dateLabel = dateOrder[0];
    return {
      labels: ["", dateLabel, ""],
      data: [{ x: dateLabel, y: points[0].y }],
    };
  }

  return {
    labels: dateOrder,
    data: points,
  };
};

export const buildPgiChartAxis = (sortedGraphData) => {
  const pgiSMeta = buildSeriesData(sortedGraphData, "pgi_s");
  const pgiCMeta = buildSeriesData(sortedGraphData, "pgi_c");

  return {
    labels: pgiSMeta.labels.length ? pgiSMeta.labels : pgiCMeta.labels,
    getPgiSMeta: () => pgiSMeta,
    getPgiCMeta: () => pgiCMeta,
  };
};

export const buildPgiLineDataset = (seriesMeta, style) => ({
  fill: false,
  showLine: true,
  borderWidth: 3,
  tension: 0,
  spanGaps: false,
  ...style,
  data: seriesMeta.data,
});

export const buildPgiChartOptions = ({
  seriesMeta,
  yAxisTitle,
  xAxisTitle = "Date",
}) => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      left: 8,
      right: 24,
      top: 16,
      bottom: 8,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      filter: (item) => item.parsed.y !== null,
      callbacks: {
        title: (items) => {
          const rawX = items[0]?.raw?.x;
          return rawX ? String(rawX) : "";
        },
        label: (context) =>
          `${context.dataset.label}: ${context.parsed.y}`,
      },
    },
  },
  scales: {
    x: {
      type: "category",
      labels: seriesMeta.labels,
      offset: true,
      title: {
        display: true,
        text: xAxisTitle,
        font: { weight: "bold" },
      },
      ticks: {
        autoSkip: false,
        maxRotation: 45,
        minRotation: 45,
        callback: function (value) {
          const label = this.getLabelForValue(value);
          return label || "";
        },
      },
      grid: {
        display: false,
        drawOnChartArea: false,
      },
    },
    y: {
      min: 0,
      max: 6,
      ticks: {
        stepSize: 1,
        callback: (value) => (value > 5 ? "" : value),
      },
      title: {
        display: true,
        text: yAxisTitle,
        font: { weight: "bold" },
      },
      border: {
        color: "#E53935",
      },
      grid: {
        display: false,
        drawOnChartArea: false,
      },
    },
  },
});

/** Inverted vs ALSFRS: lower PGI scores are better (greener). */
export const PGI_SCORE_COLOR_MAP = {
  0: "four_color",
  1: "four_color",
  2: "three_color",
  3: "three_color",
  4: "two_color",
  5: "one_color",
  6: "one_color",
  7: "one_color",
};
